import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Card, Badge, EmptyState } from "@/components/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, LifeBuoy } from "lucide-react";
import { toast } from "sonner";

const REASONS = ["Low Readiness %", "Failed Assessment", "Manager-Reported"];
const STATUSES = ["In Progress", "Improved", "No Change", "Escalated to Department Head"];

const emptyForm = () => ({
  member_id: "", department_id: "", reason: "Low Readiness %", plan: "",
  sessions_scheduled: 0, sessions_completed: 0, reassessment_date: "", status: "In Progress", notes: "",
});

export default function LowPerformers() {
  const { departments, deptName } = useApp();
  const [plans, setPlans] = useState([]);
  const [members, setMembers] = useState({});
  const [deptMembers, setDeptMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => {
    api.get("/improvement-plans").then((r) => setPlans(r.data)).catch(() => {});
    api.get("/team-members").then((r) => { const m = {}; r.data.forEach((x) => (m[x.id] = x)); setMembers(m); });
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!form.department_id) { setDeptMembers([]); return; }
    api.get("/team-members", { params: { department_id: form.department_id } }).then((r) => setDeptMembers(r.data));
  }, [form.department_id]);

  const openNew = () => { setForm(emptyForm()); setEditId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ ...emptyForm(), ...p, reassessment_date: p.reassessment_date || "" }); setEditId(p.id); setOpen(true); };

  const save = async () => {
    if (!form.member_id || !form.department_id) { toast.error("Member and department required."); return; }
    const payload = { ...form, sessions_scheduled: Number(form.sessions_scheduled), sessions_completed: Number(form.sessions_completed), reassessment_date: form.reassessment_date || null };
    try {
      if (editId) await api.put(`/improvement-plans/${editId}`, payload);
      else await api.post("/improvement-plans", payload);
      toast.success(editId ? "Plan updated." : "Plan created.");
      setOpen(false); load();
    } catch (e) { toast.error("Failed to save."); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this improvement plan?")) return;
    await api.delete(`/improvement-plans/${id}`); toast.success("Deleted."); load();
  };

  const statusTone = (s) => (s === "Improved" ? "green" : s === "Escalated to Department Head" ? "red" : s === "No Change" ? "amber" : "gold");

  return (
    <div>
      <PageHeader
        title="Coaching & Improvement Plans"
        subtitle="Every low performer has a documented plan and a visible trail of whether coaching worked."
        action={<Button data-testid="add-plan-button" onClick={openNew} className="bg-[#C9A24B] text-[#0B1120] hover:bg-[#b8923f] gap-2"><Plus className="h-4 w-4" /> New Plan</Button>}
      />
      <Card className="p-0 overflow-hidden">
        {plans.length === 0 ? (
          <EmptyState icon={LifeBuoy} title="No improvement plans" hint="Create a coaching plan for a flagged low performer." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead>Member</TableHead><TableHead>Department</TableHead><TableHead>Reason</TableHead>
                <TableHead>Sessions</TableHead><TableHead>Re-assess</TableHead><TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {plans.map((p) => (
                <TableRow key={p.id} data-testid={`plan-row-${p.id}`} className="border-slate-800 hover:bg-slate-800/40">
                  <TableCell className="text-white">{members[p.member_id]?.name || "—"}</TableCell>
                  <TableCell className="text-slate-400 text-sm">{deptName(p.department_id)}</TableCell>
                  <TableCell><Badge tone="slate">{p.reason}</Badge></TableCell>
                  <TableCell className="font-mono text-slate-300 text-sm">{p.sessions_completed}/{p.sessions_scheduled}</TableCell>
                  <TableCell className="font-mono text-slate-500 text-xs">{p.reassessment_date || "—"}</TableCell>
                  <TableCell><Badge tone={statusTone(p.status)}>{p.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    <button onClick={() => openEdit(p)} className="text-slate-400 hover:text-[#C9A24B] mr-3" data-testid={`edit-plan-${p.id}`}><Pencil className="h-4 w-4 inline" /></button>
                    <button onClick={() => remove(p.id)} className="text-slate-400 hover:text-[#DC2626]" data-testid={`delete-plan-${p.id}`}><Trash2 className="h-4 w-4 inline" /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F172A] border-slate-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Edit Plan" : "New Improvement Plan"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Department</Label>
                <Select value={form.department_id} onValueChange={(v) => setForm({ ...form, department_id: v, member_id: "" })}>
                  <SelectTrigger data-testid="plan-department" className="border-slate-700"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Member</Label>
                <Select value={form.member_id} onValueChange={(v) => setForm({ ...form, member_id: v })}>
                  <SelectTrigger data-testid="plan-member" className="border-slate-700"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{deptMembers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Reason Flagged</Label>
              <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
                <SelectTrigger className="border-slate-700"><SelectValue /></SelectTrigger>
                <SelectContent>{REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Improvement Plan</Label><Textarea value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className="border-slate-700" placeholder="Documented path to improvement…" /></div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Scheduled</Label><Input type="number" value={form.sessions_scheduled} onChange={(e) => setForm({ ...form, sessions_scheduled: e.target.value })} className="border-slate-700" /></div>
              <div><Label>Completed</Label><Input type="number" value={form.sessions_completed} onChange={(e) => setForm({ ...form, sessions_completed: e.target.value })} className="border-slate-700" /></div>
              <div><Label>Re-assess</Label><Input type="date" value={form.reassessment_date} onChange={(e) => setForm({ ...form, reassessment_date: e.target.value })} className="border-slate-700" /></div>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="border-slate-700"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Trainer Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="border-slate-700" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-plan-button" onClick={save} className="bg-[#C9A24B] text-[#0B1120] hover:bg-[#b8923f]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
